import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useResource, formatDate, formatDateTime, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { participantQuickCreateFields, personLabel, companyCreateFields } from "../lib/formOptions";
import type { Participant, Referral } from "../lib/types";

export interface CareCase {
    id: string;
    ref_code: string | null;
    participant_id: string;
    company_id: string | null;
    referral_id: string | null;
    coordinator_employee_id: string | null;
    status: "open" | "on_hold" | "closed";
    opened_on: string;
    closed_on: string | null;
    summary: string | null;
    created_at: string;
}

interface CaseSummary {
    case: CareCase & {
        participant_name: string;
        company_name: string | null;
        referral_ref: string | null;
        coordinator_name: string | null;
    };
    shifts: {
        total: number;
        completedHours: number;
        recent: Array<{ id: string; starts_at: string; ends_at: string; status: string; employee_name: string | null }>;
    };
    notes: {
        total: number;
        recent: Array<{ id: string; note_date: string; kind: string; subject: string; author_name: string | null }>;
    };
    incidents: {
        total: number;
        recent: Array<{ id: string; title: string; severity: string; status: string; occurred_at: string }>;
    };
    invoices: Array<{ id: string; invoice_number: string; status: string; period_start: string; period_end: string; total: string; company_name: string | null }>;
}

const statusChip = (s: CareCase["status"]) =>
    s === "open" ? "pass" : s === "on_hold" ? "warn" : "mute";

/**
 * A case is one episode of care — participant + provider + originating
 * referral. The file view aggregates the episode's shifts, notes, incidents
 * and invoices read-only; the records themselves stay in their own modules.
 */
export const Cases = () => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";

    const cases = useResource<CareCase[]>("/cases");
    const participants = useResource<Participant[]>("/participants");
    const companies = useResource<Array<{ id: string; name: string }>>("/companies");
    const referrals = useResource<Referral[]>("/referrals");
    const employees = useResource<Array<{ id: string; full_name: string }>>("/employees");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<CareCase | null>(null);
    const [openFileId, setOpenFileId] = useState<string | null>(null);

    const refresh = () => { cases.reload(); setCreating(false); setEditing(null); };

    const participantName = useMemo(() => {
        const map = new Map((participants.data ?? []).map((p) => [p.id, p.full_name]));
        return (id: string | null) => (id ? map.get(id) ?? "Unknown" : null);
    }, [participants.data]);
    const companyName = useMemo(() => {
        const map = new Map((companies.data ?? []).map((c) => [c.id, c.name]));
        return (id: string | null) => (id ? map.get(id) ?? "Unknown" : null);
    }, [companies.data]);

    const fields: Field[] = [
        { name: "participant_id", label: "Participant", kind: "select", required: true, half: true,
          options: (participants.data ?? []).map((p) => ({ value: p.id, label: p.full_name })),
          addNew: { title: "New participant", path: "/participants",
              fields: participantQuickCreateFields(), optionLabel: personLabel } },
        { name: "company_id", label: "Provider organisation", kind: "select", half: true,
          options: (companies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
          addNew: { title: "New organisation", path: "/companies", fields: companyCreateFields() },
          help: "Leave empty for self-managed or direct participants." },
        { name: "referral_id", label: "Originating referral", kind: "select", half: true,
          options: (referrals.data ?? []).map((r) => ({
              value: r.id,
              label: `${r.ref_code ?? "referral"} — ${r.referring_contact_name}`,
          })) },
        { name: "coordinator_employee_id", label: "Coordinator", kind: "searchselect", half: true,
          options: (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name })) },
        { name: "status", label: "Status", kind: "select", half: true, options: [
            { value: "open", label: "Open" },
            { value: "on_hold", label: "On hold" },
            { value: "closed", label: "Closed" },
        ] },
        { name: "opened_on", label: "Opened", kind: "date", half: true },
        { name: "closed_on", label: "Closed", kind: "date", half: true,
          help: "Required when the status is Closed." },
        { name: "summary", label: "Summary", kind: "textarea",
          help: "What this episode covers — services, goals, anything the file should say up front." },
    ];

    const all = cases.data ?? [];
    const filtered = all.filter((c) =>
        matches(search, c.ref_code, participantName(c.participant_id), companyName(c.company_id)) &&
        (!filters.status || c.status === filters.status)
    );

    if (cases.error) return <div className="error-banner">{cases.error}</div>;

    return (
        <div>
            {creating && (
                <EntityForm title="Open a case" path="/cases" fields={fields}
                    subtitle="One case per episode of care: this participant, with this provider, from this referral."
                    onClose={() => setCreating(false)} onSaved={refresh} />
            )}
            {editing && (
                <EntityForm title={`Edit case ${editing.ref_code ?? ""}`} path="/cases" fields={fields}
                    initial={editing} onClose={() => setEditing(null)} onSaved={refresh} />
            )}
            {openFileId && (
                <CaseFile caseId={openFileId} onClose={() => setOpenFileId(null)} />
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search cases…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "status", allLabel: "All Status", options: [
                        { value: "open", label: "Open" },
                        { value: "on_hold", label: "On hold" },
                        { value: "closed", label: "Closed" },
                    ] },
                ]}
                action={canManage && (
                    <button className="btn" onClick={() => setCreating(true)}>+ Open Case</button>
                )}
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Case</th><th>Participant</th><th>Provider</th>
                                    <th>Coordinator</th><th>Opened</th><th>Status</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => (
                                    <tr key={c.id} style={{ cursor: "pointer" }}
                                        onClick={() => setOpenFileId(c.id)}>
                                        <td className="mono" style={{ fontWeight: 600 }}>{c.ref_code ?? "—"}</td>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                <InitialsAvatar name={participantName(c.participant_id)} size={26} />
                                                {participantName(c.participant_id)}
                                            </div>
                                        </td>
                                        <td>{companyName(c.company_id) ?? <span className="chip mute">direct</span>}</td>
                                        <td>{(employees.data ?? []).find((e) => e.id === c.coordinator_employee_id)?.full_name ?? "—"}</td>
                                        <td className="mono">{formatDate(c.opened_on)}</td>
                                        <td><span className={`chip ${statusChip(c.status)}`}>{titleCase(c.status)}</span></td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <div className="row-actions">
                                                <button className="icon-btn" onClick={() => setOpenFileId(c.id)}>Open file</button>
                                                {canManage && (
                                                    <button className="icon-btn" onClick={() => setEditing(c)}>Edit</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="empty">
                                        {all.length === 0
                                            ? "No cases yet — open one to start an episode of care."
                                            : "No cases match."}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CaseFile = ({ caseId, onClose }: { caseId: string; onClose: () => void }) => {
    const [summary, setSummary] = useState<CaseSummary | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let live = true;
        api.get<CaseSummary>(`/cases/${caseId}/summary`)
            .then((data) => { if (live) setSummary(data); })
            .catch((err) => { if (live) setError(err instanceof Error ? err.message : "Could not load the case"); });
        return () => { live = false; };
    }, [caseId]);

    const c = summary?.case;
    return (
        <Modal title={c ? `Case file ${c.ref_code}` : "Case file"} width="wide"
            subtitle={c ? `${c.participant_name}${c.company_name ? ` · ${c.company_name}` : " · direct"}` : undefined}
            onClose={onClose}
            footer={<button className="btn ghost" onClick={onClose}>Close</button>}>
            {error && <div className="error-banner">{error}</div>}
            {!summary && !error && <div className="empty">Loading the file…</div>}
            {summary && c && (
                <div style={{ display: "grid", gap: "0.9rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                        <span className={`chip ${statusChip(c.status)}`}>{titleCase(c.status)}</span>
                        <span className="chip mute">Opened {formatDate(c.opened_on)}</span>
                        {c.closed_on && <span className="chip mute">Closed {formatDate(c.closed_on)}</span>}
                        {c.referral_ref && <span className="chip info mono">{c.referral_ref}</span>}
                        {c.coordinator_name && <span className="chip info">Coordinator: {c.coordinator_name}</span>}
                    </div>
                    {c.summary && (
                        <p style={{ fontSize: "0.86rem", color: "var(--ink-soft)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                            {c.summary}
                        </p>
                    )}

                    <div className="stat-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.6rem" }}>
                        <div className="card" style={{ padding: "0.7rem 0.9rem" }}>
                            <div style={{ fontSize: "1.25rem", fontWeight: 700 }}>{summary.shifts.total}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>Shifts · {summary.shifts.completedHours.toFixed(1)}h delivered</div>
                        </div>
                        <div className="card" style={{ padding: "0.7rem 0.9rem" }}>
                            <div style={{ fontSize: "1.25rem", fontWeight: 700 }}>{summary.notes.total}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>Progress notes</div>
                        </div>
                        <div className="card" style={{ padding: "0.7rem 0.9rem" }}>
                            <div style={{ fontSize: "1.25rem", fontWeight: 700 }}>{summary.incidents.total}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>Incidents</div>
                        </div>
                        <div className="card" style={{ padding: "0.7rem 0.9rem" }}>
                            <div style={{ fontSize: "1.25rem", fontWeight: 700 }}>{summary.invoices.length}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>Invoices in window</div>
                        </div>
                    </div>

                    <FileSection title={`Recent shifts (${summary.shifts.recent.length} of ${summary.shifts.total})`}>
                        {summary.shifts.recent.map((s) => (
                            <div key={s.id} className="file-row">
                                <span className="mono">{formatDateTime(s.starts_at)}</span>
                                <span>{s.employee_name ?? "unassigned"}</span>
                                <span className={`chip ${s.status === "completed" ? "pass" : s.status === "cancelled" ? "mute" : "info"}`}>{titleCase(s.status)}</span>
                            </div>
                        ))}
                        {summary.shifts.recent.length === 0 && <div className="empty">No shifts in this episode yet.</div>}
                    </FileSection>

                    <FileSection title={`Progress notes (${summary.notes.recent.length} of ${summary.notes.total})`}>
                        {summary.notes.recent.map((n) => (
                            <div key={n.id} className="file-row">
                                <span className="mono">{formatDate(n.note_date)}</span>
                                <span>{n.subject}</span>
                                <span style={{ color: "var(--muted)", fontSize: "0.75rem" }}>{n.author_name ?? ""}</span>
                            </div>
                        ))}
                        {summary.notes.recent.length === 0 && <div className="empty">No notes in this episode yet.</div>}
                    </FileSection>

                    <FileSection title={`Incidents (${summary.incidents.total})`}>
                        {summary.incidents.recent.map((i) => (
                            <div key={i.id} className="file-row">
                                <span className="mono">{formatDate(i.occurred_at)}</span>
                                <span>{i.title}</span>
                                <span className={`chip ${i.severity === "high" || i.severity === "critical" ? "block" : "warn"}`}>{titleCase(i.severity)}</span>
                            </div>
                        ))}
                        {summary.incidents.recent.length === 0 && <div className="empty">No incidents — good.</div>}
                    </FileSection>

                    <FileSection title={`Invoices touching this episode (${summary.invoices.length})`}>
                        {summary.invoices.map((inv) => (
                            <div key={inv.id} className="file-row">
                                <span className="mono">{inv.invoice_number}</span>
                                <span>{formatDate(inv.period_start)} – {formatDate(inv.period_end)}</span>
                                <span className="mono">${Number(inv.total).toFixed(2)}</span>
                                <span className={`chip ${inv.status === "paid" ? "pass" : "info"}`}>{titleCase(inv.status)}</span>
                            </div>
                        ))}
                        {summary.invoices.length === 0 && <div className="empty">Nothing billed in this window yet.</div>}
                    </FileSection>
                </div>
            )}
        </Modal>
    );
};

const FileSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div>
        <h3 style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em",
            color: "var(--muted)", margin: "0 0 0.4rem" }}>{title}</h3>
        <div style={{ display: "grid", gap: "0.3rem" }}>{children}</div>
    </div>
);
